import React from 'react';
import { motion } from 'framer-motion';
import { TerminalIcon, NetworkIcon, CpuIcon, ArrowRightIcon, CodeIcon } from 'lucide-react';
import { SlideLayout, itemVariants } from '../ui/SlideLayout';
export function HowItWorksSlide() {
  return <SlideLayout data-id="element-202">
      <motion.div variants={itemVariants} className="mb-16 text-center" data-id="element-203">
        <h2 className="text-4xl md:text-5xl font-bold text-brand-text mb-4" data-id="element-204">
          A Cloud API Powered by High-Performance FPGAs
        </h2>
      </motion.div>

      {/* Architecture Diagram */}
      <motion.div variants={itemVariants} className="flex flex-col md:flex-row items-center justify-between gap-4 mb-20 relative" data-id="element-205">
        {/* Connecting Line Background (Desktop) */}
        <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-brand-border -translate-y-1/2 z-0" data-id="element-206" />

        {/* Step 1 */}
        <div className="bg-brand-bg-card border border-brand-border rounded-xl p-6 w-full md:w-64 relative z-10 flex flex-col items-center text-center" data-id="element-207">
          <div className="w-12 h-12 bg-brand-bg rounded-full flex items-center justify-center border border-brand-border mb-4" data-id="element-208">
            <TerminalIcon className="w-6 h-6 text-brand-text" data-id="element-209" />
          </div>
          <h3 className="text-lg font-bold text-brand-text mb-2" data-id="element-210">Your App</h3>
          <p className="text-sm text-brand-text-dim" data-id="element-211">Python, curl, SDKs</p>
          <div className="absolute -bottom-3 md:-right-6 md:top-1/2 md:-translate-y-1/2 md:bottom-auto bg-brand-bg p-1 rounded-full border border-brand-border md:border-none" data-id="element-212">
            <ArrowRightIcon className="w-5 h-5 text-brand-text-muted rotate-90 md:rotate-0" data-id="element-213" />
          </div>
        </div>

        {/* Step 2 */}
        <div className="bg-brand-bg-card border border-brand-teal rounded-xl p-6 w-full md:w-64 relative z-10 flex flex-col items-center text-center shadow-[0_0_20px_rgba(0,212,170,0.1)]" data-id="element-214">
          <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center border border-brand-teal/30 mb-4" data-id="element-215">
            <NetworkIcon className="w-6 h-6 text-brand-teal" data-id="element-216" />
          </div>
          <h3 className="text-lg font-bold text-brand-text mb-2" data-id="element-217">
            OpenFPGA API
          </h3>
          <span className="px-2 py-1 bg-brand-teal/10 text-brand-teal text-xs rounded-md font-mono border border-brand-teal/20" data-id="element-218">
            OpenAI-Compatible
          </span>
          <div className="absolute -bottom-3 md:-right-6 md:top-1/2 md:-translate-y-1/2 md:bottom-auto bg-brand-bg p-1 rounded-full border border-brand-border md:border-none" data-id="element-219">
            <ArrowRightIcon className="w-5 h-5 text-brand-text-muted rotate-90 md:rotate-0" data-id="element-220" />
          </div>
        </div>

        {/* Step 3 */}
        <div className="bg-brand-bg-card border border-brand-border rounded-xl p-6 w-full md:w-64 relative z-10 flex flex-col items-center text-center" data-id="element-221">
          <div className="w-12 h-12 bg-brand-bg rounded-full flex items-center justify-center border border-brand-border mb-4" data-id="element-222">
            <CodeIcon className="w-6 h-6 text-brand-text" data-id="element-223" />
          </div>
          <h3 className="text-lg font-bold text-brand-text mb-2" data-id="element-224">
            OpenFPGA Forge
          </h3>
          <p className="text-sm text-brand-text-dim" data-id="element-225">Compiler + Scheduler</p>
          <div className="absolute -bottom-3 md:-right-6 md:top-1/2 md:-translate-y-1/2 md:bottom-auto bg-brand-bg p-1 rounded-full border border-brand-border md:border-none" data-id="element-226">
            <ArrowRightIcon className="w-5 h-5 text-brand-text-muted rotate-90 md:rotate-0" data-id="element-227" />
          </div>
        </div>

        {/* Step 4 */}
        <div className="bg-brand-bg-card border border-brand-border rounded-xl p-6 w-full md:w-64 relative z-10 flex flex-col items-center text-center" data-id="element-228">
          <div className="w-12 h-12 bg-brand-bg rounded-full flex items-center justify-center border border-brand-border mb-4" data-id="element-229">
            <CpuIcon className="w-6 h-6 text-brand-text" data-id="element-230" />
          </div>
          <h3 className="text-lg font-bold text-brand-text mb-2" data-id="element-231">
            FPGA Cluster
          </h3>
          <p className="text-sm text-brand-text-dim" data-id="element-232">32GB on-chip RAM</p>
        </div>
      </motion.div>

      {/* Key Points */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-233">
        <motion.div variants={itemVariants} className="space-y-6" data-id="element-234">
          <div data-id="element-235">
            <h4 className="text-brand-teal font-semibold mb-1 flex items-center gap-2" data-id="element-236">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-teal" data-id="element-237" />{' '}
              Standard REST API
            </h4>
            <p className="text-brand-text-dim text-sm" data-id="element-238">
              Call the same format you already use with OpenAI, OpenRouter, or
              Together AI. Select a model, send a request, receive tokens.
            </p>
          </div>
          <div data-id="element-239">
            <h4 className="text-brand-teal font-semibold mb-1 flex items-center gap-2" data-id="element-240">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-teal" data-id="element-241" />{' '}
              Proprietary Compiler
            </h4>
            <p className="text-brand-text-dim text-sm" data-id="element-242">
              OpenFPGA Forge optimizes and synthesizes each model directly onto
              FPGA hardware — quantization, dataflow mapping, multi-card
              parallelism.
            </p>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="space-y-6" data-id="element-243">
          <div data-id="element-244">
            <h4 className="text-brand-teal font-semibold mb-1 flex items-center gap-2" data-id="element-245">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-teal" data-id="element-246" />{' '}
              Massive On-Chip Memory
            </h4>
            <p className="text-brand-text-dim text-sm" data-id="element-247">
              32 GB of on-chip RAM per card runs an 8B model entirely on-chip.
              Larger models (70B, 430B) distribute automatically.
            </p>
          </div>
          <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4" data-id="element-248">
            <h4 className="text-brand-text font-semibold mb-1" data-id="element-249">
              Zero Hardware Management
            </h4>
            <p className="text-brand-text-dim text-sm" data-id="element-250">
              No FPGA knowledge required. Integration is a single API-key swap.
              You only touch the left side of the diagram.
            </p>
          </div>
        </motion.div>
      </div>
    </SlideLayout>;
}